// Broadcast messages shown in the app — promotions, maintenance notices,
// "please update". The app fetches the live ones (GET /api/announcements) on
// launch/resume; this page writes them via /api/admin/announcements.
import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { AdminShell } from './AdminShell';
import { api, ApiException } from '../../lib/api';
import { Button } from '../../components/Button';
import { Table, Th, Td, Badge, fieldStyle } from './ui';

type Kind = 'info' | 'promo' | 'maintenance' | 'update';
type Display = 'banner' | 'popup';
type Platform = 'all' | 'ios' | 'android';
type Text = { title?: string; body?: string; ctaLabel?: string };

type Announcement = {
  id: number;
  kind: Kind;
  display: Display;
  title: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
  translations: Record<string, Text>;
  platform: Platform;
  maxBuild: number | null;
  dismissible: boolean;
  active: boolean;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
};

type Draft = Omit<Announcement, 'id' | 'createdAt'> & { id?: number };

const LOCALES: [string, string][] = [
  ['de', 'Deutsch'], ['es', 'Español'], ['fr', 'Français'], ['it', 'Italiano'], ['pt', 'Português'],
];

const KIND_LABEL: Record<Kind, string> = {
  info: 'Info', promo: 'Promotion', maintenance: 'Maintenance', update: 'App update',
};

// Mirrors the app's styling per kind, so the preview reads like the real thing.
const KIND_STYLE: Record<Kind, { icon: string; accent: string; bg: string }> = {
  info: { icon: 'ℹ︎', accent: 'var(--ink-2)', bg: 'var(--surface-2)' },
  promo: { icon: '✦', accent: 'var(--ember)', bg: 'var(--ember-tint)' },
  maintenance: { icon: '⚙︎', accent: 'var(--leaf)', bg: 'var(--marigold-soft)' },
  update: { icon: '↑', accent: '#1c7a45', bg: 'rgba(34,160,90,0.14)' },
};

const EMPTY: Draft = {
  kind: 'info', display: 'banner', title: '', body: '', ctaLabel: '', ctaUrl: '',
  translations: {}, platform: 'all', maxBuild: null, dismissible: true, active: true,
  startsAt: null, endsAt: null,
};

export function AdminAnnouncements() {
  return <AdminShell tab="announcements">{() => <AnnouncementsPage />}</AdminShell>;
}

function AnnouncementsPage() {
  const [list, setList] = useState<Announcement[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () =>
    api<{ announcements: Announcement[] }>('/api/admin/announcements')
      .then((r) => setList(r.announcements))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));

  useEffect(() => { load(); }, []);

  const toggleActive = async (a: Announcement) => {
    try {
      await api(`/api/admin/announcements/${a.id}`, {
        method: 'PUT',
        body: JSON.stringify(toInput({ ...a, active: !a.active })),
      });
      load();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Update failed');
    }
  };

  const remove = async (a: Announcement) => {
    if (!confirm(`Delete “${a.title}”? Users who already dismissed it won't notice; everyone else stops seeing it.`)) return;
    try {
      await api(`/api/admin/announcements/${a.id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Delete failed');
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', maxWidth: 640, lineHeight: 1.5 }}>
          Messages shown in the app to everyone (signed in or not) — a banner on Home or a one-time
          popup. The app checks on launch and when it comes back to the foreground.
        </div>
        {!draft && (
          <Button variant="ember" size="sm" onClick={() => setDraft({ ...EMPTY })}>
            New announcement
          </Button>
        )}
      </div>

      {error && <div style={{ color: 'var(--wine)', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {draft && (
        <Editor
          initial={draft}
          onCancel={() => setDraft(null)}
          onSaved={() => { setDraft(null); load(); }}
        />
      )}

      <Table>
        <thead>
          <tr>
            <Th>Message</Th><Th>Type</Th><Th>Audience</Th><Th>Window</Th><Th>Status</Th><Th>{''}</Th>
          </tr>
        </thead>
        <tbody>
          {list === null ? (
            <tr><Td colSpan={6} muted>Loading…</Td></tr>
          ) : list.length === 0 ? (
            <tr><Td colSpan={6} muted>No announcements yet.</Td></tr>
          ) : list.map((a) => (
            <tr key={a.id}>
              <Td>
                <div style={{ fontWeight: 600 }}>{a.title}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.body}
                </div>
              </Td>
              <Td>{KIND_LABEL[a.kind]} · {a.display}</Td>
              <Td muted>
                {a.platform === 'all' ? 'All' : a.platform === 'ios' ? 'iOS' : 'Android'}
                {a.maxBuild != null && <> · build &lt; {a.maxBuild}</>}
              </Td>
              <Td muted>{fmtWindow(a.startsAt, a.endsAt)}</Td>
              <Td><StatusBadge a={a} /></Td>
              <Td>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <Button variant="secondary" size="sm" onClick={() => setDraft({ ...a })}>Edit</Button>
                  <Button variant="secondary" size="sm" onClick={() => toggleActive(a)}>
                    {a.active ? 'Stop' : 'Resume'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(a)}>Delete</Button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

function StatusBadge({ a }: { a: Announcement }) {
  const now = Date.now();
  if (!a.active) return <Badge>Stopped</Badge>;
  if (a.startsAt && Date.parse(a.startsAt) > now) return <Badge tone="ember">Scheduled</Badge>;
  if (a.endsAt && Date.parse(a.endsAt) <= now) return <Badge>Ended</Badge>;
  return <Badge tone="green">Live</Badge>;
}

function fmtWindow(start: string | null, end: string | null): string {
  const f = (iso: string) =>
    new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  if (!start && !end) return 'Always';
  if (!end) return `From ${f(start!)}`;
  if (!start) return `Until ${f(end)}`;
  return `${f(start)} → ${f(end)}`;
}

// The API body: everything but server-owned fields.
function toInput(d: Draft) {
  return {
    kind: d.kind, display: d.display, title: d.title, body: d.body,
    ctaLabel: d.ctaLabel, ctaUrl: d.ctaUrl, translations: d.translations,
    platform: d.platform, maxBuild: d.maxBuild, dismissible: d.dismissible,
    active: d.active, startsAt: d.startsAt, endsAt: d.endsAt,
  };
}

// <input type="datetime-local"> speaks local wall-clock time without a zone;
// the API speaks ISO instants.
function isoToLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localToIso(v: string): string | null {
  return v ? new Date(v).toISOString() : null;
}

function Editor({
  initial, onCancel, onSaved,
}: { initial: Draft; onCancel: () => void; onSaved: () => void }) {
  const [d, setD] = useState<Draft>(initial);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [previewLang, setPreviewLang] = useState('en');
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((p) => ({ ...p, [k]: v }));
  const setTr = (lang: string, k: keyof Text, v: string) =>
    setD((p) => ({ ...p, translations: { ...p.translations, [lang]: { ...p.translations[lang], [k]: v } } }));

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api(d.id ? `/api/admin/announcements/${d.id}` : '/api/admin/announcements', {
        method: d.id ? 'PUT' : 'POST',
        body: JSON.stringify(toInput(d)),
      });
      onSaved();
    } catch (e) {
      setMsg(e instanceof ApiException ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const label = (text: string, child: JSX.Element, hint?: string) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>
      {text}
      {child}
      {hint && <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>{hint}</span>}
    </label>
  );
  const input = (value: string, onInput: (v: string) => void, props: JSX.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <input
      value={value}
      onInput={(e) => onInput((e.target as HTMLInputElement).value)}
      style={fieldStyle}
      {...props}
    />
  );

  const tr = previewLang === 'en' ? {} : d.translations[previewLang] ?? {};
  const preview = {
    title: tr.title?.trim() || d.title,
    body: tr.body?.trim() || d.body,
    ctaLabel: tr.ctaLabel?.trim() || d.ctaLabel,
  };

  return (
    <div
      style={{
        background: 'var(--card)', borderRadius: 16, boxShadow: '0 0 0 1px var(--line)',
        padding: 18, marginBottom: 20, display: 'flex', gap: 24, flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 420px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{d.id ? 'Edit announcement' : 'New announcement'}</div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {label('Type', (
            <select value={d.kind} onChange={(e) => set('kind', (e.target as HTMLSelectElement).value as Kind)} style={fieldStyle}>
              {(Object.keys(KIND_LABEL) as Kind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
            </select>
          ))}
          {label('Shown as', (
            <select value={d.display} onChange={(e) => set('display', (e.target as HTMLSelectElement).value as Display)} style={fieldStyle}>
              <option value="banner">Banner on Home</option>
              <option value="popup">Popup (once)</option>
            </select>
          ))}
          {label('Platform', (
            <select value={d.platform} onChange={(e) => set('platform', (e.target as HTMLSelectElement).value as Platform)} style={fieldStyle}>
              <option value="all">iOS + Android</option>
              <option value="ios">iOS only</option>
              <option value="android">Android only</option>
            </select>
          ))}
          {label('Only builds below', input(
            d.maxBuild == null ? '' : String(d.maxBuild),
            (v) => set('maxBuild', v.trim() === '' ? null : Number(v)),
            { type: 'number', min: 1, placeholder: 'any', style: { ...fieldStyle, width: 110 } },
          ), 'e.g. 25 = nag builds ≤ 24')}
        </div>

        {label('Title (English)', input(d.title, (v) => set('title', v), { maxLength: 120, placeholder: 'Scheduled maintenance tonight' }))}
        {label('Message (English)', (
          <textarea
            value={d.body}
            onInput={(e) => set('body', (e.target as HTMLTextAreaElement).value)}
            maxLength={2000}
            rows={3}
            placeholder="Sync may be unavailable between 1:00 and 2:00. Logging still works offline."
            style={{ ...fieldStyle, resize: 'vertical' }}
          />
        ))}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {label('Button label', input(d.ctaLabel, (v) => set('ctaLabel', v), { maxLength: 40, placeholder: d.kind === 'update' ? 'Update now' : 'optional' }))}
          {label('Button link', input(d.ctaUrl, (v) => set('ctaUrl', v), { placeholder: 'https://…', style: { ...fieldStyle, minWidth: 260 } }),
            d.kind === 'update' ? 'Leave empty to open the App Store / Play Store listing.' : undefined)}
        </div>

        <details>
          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>
            Translations (optional — empty fields fall back to English)
          </summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
            {LOCALES.map(([code, name]) => (
              <div key={code} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ width: 80, fontSize: 12, fontWeight: 600, paddingTop: 10 }}>{name}</div>
                {input(d.translations[code]?.title ?? '', (v) => setTr(code, 'title', v), { placeholder: 'Title', maxLength: 120, style: { ...fieldStyle, flex: '1 1 160px' } })}
                {input(d.translations[code]?.body ?? '', (v) => setTr(code, 'body', v), { placeholder: 'Message', maxLength: 2000, style: { ...fieldStyle, flex: '2 1 220px' } })}
                {input(d.translations[code]?.ctaLabel ?? '', (v) => setTr(code, 'ctaLabel', v), { placeholder: 'Button', maxLength: 40, style: { ...fieldStyle, width: 110 } })}
              </div>
            ))}
          </div>
        </details>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {label('Starts', input(isoToLocal(d.startsAt), (v) => set('startsAt', localToIso(v)), { type: 'datetime-local' }), 'empty = now')}
          {label('Ends', input(isoToLocal(d.endsAt), (v) => set('endsAt', localToIso(v)), { type: 'datetime-local' }), 'empty = until stopped')}
        </div>

        <div style={{ display: 'flex', gap: 18, fontSize: 13, color: 'var(--ink-2)' }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={d.dismissible} onChange={(e) => set('dismissible', (e.target as HTMLInputElement).checked)} />
            Users can dismiss it
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={d.active} onChange={(e) => set('active', (e.target as HTMLInputElement).checked)} />
            Active
          </label>
        </div>

        {msg && <div style={{ color: 'var(--wine)', fontSize: 13 }}>{msg}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ember" size="sm" disabled={busy || !d.title.trim() || !d.body.trim()} onClick={save}>
            {d.id ? 'Save changes' : d.active ? 'Publish' : 'Save as stopped'}
          </Button>
          <Button variant="secondary" size="sm" disabled={busy} onClick={onCancel}>Cancel</Button>
        </div>
      </div>

      <div style={{ flex: '0 1 320px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Preview
          </div>
          <select value={previewLang} onChange={(e) => setPreviewLang((e.target as HTMLSelectElement).value)} style={{ ...fieldStyle, padding: '4px 8px', fontSize: 12 }}>
            <option value="en">English</option>
            {LOCALES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
        </div>
        <Preview kind={d.kind} display={d.display} dismissible={d.dismissible} {...preview} />
      </div>
    </div>
  );
}

// An approximation of the app's banner card / popup sheet on the cream
// background, so the admin sees roughly what users will.
function Preview({
  kind, display, title, body, ctaLabel, dismissible,
}: { kind: Kind; display: Display; title: string; body: string; ctaLabel: string; dismissible: boolean }) {
  const s = KIND_STYLE[kind];
  const cta = ctaLabel || (kind === 'update' ? 'Update now' : '');
  const card = (
    <div style={{ background: 'var(--card)', borderRadius: display === 'popup' ? 22 : 18, padding: 14, boxShadow: '0 6px 20px rgba(0,0,0,0.08)', position: 'relative' }}>
      {dismissible && <div style={{ position: 'absolute', top: 10, right: 12, color: 'var(--ink-3)', fontSize: 13 }}>✕</div>}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ width: 32, height: 32, borderRadius: 10, background: s.bg, color: s.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
          {s.icon}
        </div>
        <div style={{ paddingRight: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{title || 'Title'}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2, whiteSpace: 'pre-wrap' }}>{body || 'Message'}</div>
          {cta && (
            <div style={{ marginTop: 10, display: 'inline-block', background: 'var(--ink)', color: 'var(--card)', borderRadius: 99, padding: '6px 14px', fontSize: 12, fontWeight: 700 }}>
              {cta}
            </div>
          )}
        </div>
      </div>
    </div>
  );
  return (
    <div style={{ background: 'var(--cream-2, #FFE9CE)', borderRadius: 24, padding: 14, minHeight: 200, display: 'flex', flexDirection: 'column', justifyContent: display === 'popup' ? 'flex-end' : 'flex-start' }}>
      {display === 'popup' ? (
        <>
          <div style={{ flex: 1, background: 'rgba(0,0,0,0.25)', borderRadius: 12, marginBottom: 8 }} />
          {card}
        </>
      ) : (
        <>
          {card}
          <div style={{ marginTop: 10, height: 70, background: 'rgba(0,0,0,0.06)', borderRadius: 18 }} />
        </>
      )}
    </div>
  );
}
